import { add, listen, qs, qsa, remove } from "@fluorescent/dom";
import { disableBodyScroll, enableBodyScroll } from "body-scroll-lock";
import { createFocusTrap } from "focus-trap";

import DrawerSearch from "@/lib/drawer-search";
import { prefersReducedMotion } from "@/lib/a11y";
import { FadeUpReveal } from "@/lib/animation";
import { emit } from "evx";

const sel = {
  overlay: "[data-overlay]",
  listItem: "[data-list-item]",
  item: "[data-item]",
  allLinks: "[data-all-links]",
  main: "[data-main]",
  primary: "[data-primary-container]",
  searchMenu: "[data-search-menu]",
  animationItems:
    ".drawer-menu__primary-links > .drawer-menu__item, .drawer-menu__primary-links > .drawer-menu__form",

  // Cross border
  form: ".drawer-menu-ojo__lang-panel",
  localeInput: "[data-locale-input]",
  currencyInput: "[data-currency-input]",

  // Child panels
  childPanel: "[data-child-panel]",
  childBack: "[data-child-back]",
};

const classes = {
  active: "active",
  visible: "visible",
  childVisible: "child-visible",
  childActive: "child-active",
};

const { enableRevealAnimations } = document.body.dataset;

const menu = node => {
  const focusTrap = createFocusTrap(node, {
    allowOutsideClick: true,
  });

  // This is the element that holds the one we move left and right (primary)
  const main = qs(sel.main, node);

  // Element that holds all the primary links
  const primary = qs(sel.primary, node);

  // Cross border
  const form = qs(sel.form, node);
  const localeInput = qs(sel.localeInput, node);
  const currencyInput = qs(sel.currencyInput, node);

  const search = DrawerSearch(node, () => {});

  // Nodes
  const overlay = qs(sel.overlay, node);
  const closeButtons = qsa("[data-drawer-close]", node);
  const animationItems = qsa(sel.animationItems, node);

  // Primary parent links (items with data-child-index)
  const parentLinks = qsa('[data-child-index]', node);

  // Child panels
  const childPanels = qsa(sel.childPanel, node);
  const childBacks = qsa(sel.childBack, node);

  // Language/Currency panel (custom dropdown approach)
  const langPanel = qs('.drawer-menu-ojo__lang-panel', node);
  const langPanelOpener = qs('[data-lang-panel-open]', node);
  const langPanelCloseBtns = qsa('[data-lang-panel-close]', node);
  const langConfirmBtn = qs('[data-lang-confirm]', node);

  // Custom dropdown triggers and items
  const dropdownTriggers = qsa('[data-dropdown-trigger]', node);
  const dropdownItems = qsa('[data-dropdown-item]', node);

  // Contact Us panel
  const contactPanel = qs('.drawer-menu-ojo__contact-panel', node);
  const contactPanelOpeners = qsa('[data-contact-panel-open]');
  const contactPanelCloseBtns = qsa('[data-contact-panel-close]', node);
  const contactForm = qs('.drawer-menu-ojo__contact-form', node);
  const contactFormView = qs('[data-contact-form-view]', node);
  const contactSuccessView = qs('[data-contact-success-view]', node);
  const contactReturnBtn = qs('[data-contact-return]', node);

  // Init animation
  const reveals = [];
  initRevealAnimation();

  // Mobile header search & cart inside drawer (main panel + child panels)
  const allMobileSearchBtns = qsa('[data-search]', node);
  const allMobileCartBtns = qsa('[data-js-cart-icon]', node);
  const drawerCartOpenBtn = qs('[data-drawer-cart-open]', node);

  // Original header search & cart (outside drawer, in the site header)
  const headerNode = node.closest('.header') || node.closest('header');
  const originalSearch = headerNode ? qs('.header-ojo__left [data-search]', headerNode) : null;
  const originalCart = headerNode ? qs('.header-ojo__right [data-js-cart-icon]', headerNode) : null;

  const events = [
    // Click on overlay or close button
    listen(overlay, "click", close),
    listen(closeButtons, "click", close),

    // Esq pressed
    listen(node, "keydown", ({ keyCode }) => {
      if (keyCode === 27) close();
    }),

    // Primary parent menu items → open child panel
    listen(parentLinks, "click", clickParent),

    // Child panel back buttons
    listen(childBacks, "click", clickChildBack),

    // Language/Currency panel open
    ...(langPanelOpener ? [listen(langPanelOpener, "click", openLangPanel)] : []),

    // Language/Currency panel close buttons
    listen(langPanelCloseBtns, "click", closeLangPanel),

    // Language/Currency CONFIRM button
    ...(langConfirmBtn ? [listen(langConfirmBtn, "click", confirmLangCurrency)] : []),

    // Custom dropdown triggers
    listen(dropdownTriggers, "click", toggleDropdown),

    // Custom dropdown items
    listen(dropdownItems, "click", selectDropdownItem),

    // Contact panel open
    ...contactPanelOpeners.map(opener => listen(opener, "click", (e) => {
      e.preventDefault();
      open();
      setTimeout(() => openContactPanel(e), 50);
    })),

    // Contact panel close buttons
    listen(contactPanelCloseBtns, "click", closeContactPanel),

    // Contact form — let it submit natively (captcha/CSRF handled by Shopify)

    // Contact return button → close panel & go to collections
    ...(contactReturnBtn ? [listen(contactReturnBtn, "click", contactReturn)] : []),

    // All mobile header search buttons → close drawer, then open search flyout
    listen(allMobileSearchBtns, "click", e => {
      e.preventDefault();
      close();
      setTimeout(() => {
        if (originalSearch) originalSearch.click();
      }, 700);
    }),

    // All mobile header cart buttons → close drawer, then open cart flyout
    listen(allMobileCartBtns, "click", e => {
      e.preventDefault();
      close();
      setTimeout(() => {
        emit("cart:open", { flash: false });
      }, 700);
    }),

    // Dedicated drawer cart open button (utility link at bottom)
    ...(drawerCartOpenBtn ? [listen(drawerCartOpenBtn, "click", e => {
      e.preventDefault();
      close();
      setTimeout(() => {
        emit("cart:open", { flash: false });
      }, 700);
    })] : []),
  ];

  function initRevealAnimation() {
    if (!prefersReducedMotion() && enableRevealAnimations === "true") {
      animationItems.forEach(item => {
        reveals.push(new FadeUpReveal(item, { yStart: "40px" }));
      });
    }
  }

  function open() {
    add(node, classes.active);

    if (!prefersReducedMotion() && enableRevealAnimations === "true") {
      reveals.forEach((reveal, i) => {
        reveal.play(window.theme.animation.delayExtraShort * i);
      });
    }

    setTimeout(() => {
      add(node, classes.visible);
      focusTrap.activate();

      disableBodyScroll(node, {
        allowTouchMove: el => {
          while (el && el !== document.body) {
            if (el.getAttribute("data-scroll-lock-ignore") !== null) {
              return true;
            }
            el = el.parentNode;
          }
        },
        reserveScrollBarGap: true,
      });
    }, 50);
  }

  function close() {
    focusTrap.deactivate();
    remove(node, classes.visible);

    // Close all child panels & lang panel & contact panel
    closeAllChildPanels();
    closeLangPanel();
    closeContactPanel();

    setTimeout(() => {
      remove(node, classes.active);
      enableBodyScroll(node);
      reveals.forEach(reveal => reveal.init());
    }, 660);
  }

  function clickParent(e) {
    e.preventDefault();

    const link = e.currentTarget;
    const childIndex = link.getAttribute("data-child-index");
    const panel = qs(`[data-child-panel="${childIndex}"]`, node);

    if (!panel) return;

    // Toggle: if already open, close it
    const isOpen = panel.classList.contains(classes.visible);
    closeAllChildPanels();

    if (isOpen) return;

    // Open the target child panel
    add(panel, classes.visible);
    panel.setAttribute("aria-hidden", "false");

    // Mark the parent li as active (bold text)
    add(link.closest(".drawer-menu__item"), classes.childActive);

    link.setAttribute("aria-expanded", "true");
  }

  function clickChildBack(e) {
    e.preventDefault();

    const panel = e.currentTarget.closest("[data-child-panel]");
    if (!panel) return;

    const childIndex = panel.getAttribute("data-child-panel");

    // Close the child panel
    remove(panel, classes.visible);
    panel.setAttribute("aria-hidden", "true");

    // Remove active state from parent
    const parentLink = qs(`[data-child-index="${childIndex}"]`, node);
    if (parentLink) {
      remove(parentLink.closest(".drawer-menu__item"), classes.childActive);
      parentLink.setAttribute("aria-expanded", "false");
    }
  }

  function closeAllChildPanels() {
    childPanels.forEach(panel => {
      remove(panel, classes.visible);
      panel.setAttribute("aria-hidden", "true");
    });

    // Remove child-active from all parent items
    qsa(`.${classes.childActive}`, node).forEach(el => {
      remove(el, classes.childActive);
    });

    // Reset all aria-expanded
    parentLinks.forEach(link => {
      link.setAttribute("aria-expanded", "false");
    });
  }

  // Language/Currency panel
  function openLangPanel(e) {
    e.preventDefault();
    if (langPanel) {
      closeAllChildPanels();
      add(langPanel, classes.visible);
      add(node, "lang-panel-active");
    }
  }

  function closeLangPanel(e) {
    if (e) e.preventDefault();
    if (langPanel) {
      remove(langPanel, classes.visible);
      remove(node, "lang-panel-active");
    }
  }

  // Custom dropdown — toggle open/close
  function toggleDropdown(e) {
    e.preventDefault();
    const trigger = e.currentTarget;
    const type = trigger.getAttribute('data-dropdown-trigger');
    const list = qs(`[data-dropdown-list="${type}"]`, node);
    if (!list) return;

    const isOpen = trigger.getAttribute('aria-expanded') === 'true';

    // Close all dropdowns first
    closeAllDropdowns();

    if (!isOpen) {
      trigger.setAttribute('aria-expanded', 'true');
      list.setAttribute('aria-hidden', 'false');
    }
  }

  // Custom dropdown — select an item
  function selectDropdownItem(e) {
    e.preventDefault();
    const item = e.currentTarget;
    const list = item.closest('[data-dropdown-list]');
    const type = list.getAttribute('data-dropdown-list');
    const trigger = qs(`[data-dropdown-trigger="${type}"]`, node);
    const label = qs('[data-dropdown-label]', trigger);

    // Update selected state
    qsa('[data-dropdown-item]', list).forEach(el => remove(el, 'is-selected'));
    add(item, 'is-selected');

    // Update trigger label
    if (label) {
      label.textContent = item.textContent.trim();
    }

    // Close the dropdown
    trigger.setAttribute('aria-expanded', 'false');
    list.setAttribute('aria-hidden', 'true');
  }

  // Close all open custom dropdowns
  function closeAllDropdowns() {
    qsa('[data-dropdown-trigger]', node).forEach(trigger => {
      trigger.setAttribute('aria-expanded', 'false');
    });
    qsa('[data-dropdown-list]', node).forEach(list => {
      list.setAttribute('aria-hidden', 'true');
    });
  }

  function confirmLangCurrency(e) {
    e.preventDefault();

    // Read selected values from custom dropdowns
    const selectedLang = qs('[data-dropdown-list="lang"] .is-selected', node);
    const selectedCurrency = qs('[data-dropdown-list="currency"] .is-selected', node);

    if (selectedLang && localeInput) {
      localeInput.value = selectedLang.getAttribute('data-value');
    }
    if (selectedCurrency && currencyInput) {
      currencyInput.value = selectedCurrency.getAttribute('data-value');
    }

    // Submit the localization form
    if (form) {
      close();
      form.submit();
    }
  }

  // Contact Us panel
  function openContactPanel(e) {
    e.preventDefault();
    if (contactPanel) {
      closeAllChildPanels();
      closeLangPanel();
      add(contactPanel, classes.visible);
      add(node, "contact-panel-active");
    }
  }

  function closeContactPanel(e) {
    if (e) e.preventDefault();
    if (contactPanel) {
      remove(contactPanel, classes.visible);
      remove(node, "contact-panel-active");
    }
  }

  // Check if we landed on ?contact_posted=true → auto-open success panel
  if (window.location.search.includes('contact_posted=true')) {
    setTimeout(() => {
      open();
      setTimeout(() => {
        if (contactPanel) {
          closeAllChildPanels();
          closeLangPanel();
          add(contactPanel, classes.visible);
          add(node, 'contact-panel-active');
          if (contactFormView) contactFormView.style.display = 'none';
          if (contactSuccessView) contactSuccessView.style.display = '';
        }
        // Clean up URL
        const url = new URL(window.location);
        url.searchParams.delete('contact_posted');
        url.hash = '';
        window.history.replaceState({}, '', url.pathname + url.search);
      }, 100);
    }, 300);
  }

  function contactReturn(e) {
    e.preventDefault();
    close();
    // Reset views for next open
    if (contactFormView) contactFormView.style.display = "";
    if (contactSuccessView) contactSuccessView.style.display = "none";
    setTimeout(() => {
      window.location.href = "/collections";
    }, 700);
  }

  function destroy() {
    events.forEach(unsubscribe => unsubscribe());
    enableBodyScroll(node);
    search.destroy();
  }

  return { close, destroy, open };
};

export default menu;
